import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { postSupportMessage, postSupportMessageMedia } from '../../api/zabota_chat_api';
import { getSupportChatErrorMessage, validateAttachment } from './utils';

/**
 * Owns the message composer state: text input, a single pending attachment
 * and the send flow (text-only, attachment-only or attachment + caption).
 *
 * Sending a message adds an optimistic "pending" bubble via `addPendingMessage`
 * so it shows up instantly, instead of waiting for the send request (which can
 * take a while — it forwards to Telegram synchronously) and the next poll.
 */
export function useSupportChatComposer({ cabinetUserId, fio, onSent, addPendingMessage, removePendingMessage }) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationError = validateAttachment(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const removeSelectedFile = () => setSelectedFile(null);

  const send = async () => {
    const caption = text.trim();
    if (!selectedFile && !caption) {
      toast.error('Заполните сообщение или прикрепите файл');
      return;
    }

    if (!cabinetUserId || !fio) {
      toast.error('Не удалось определить пользователя');
      return;
    }

    const pendingId = addPendingMessage({
      sender_role: 'user',
      body: caption,
      created_at: new Date().toISOString(),
      pendingFile: selectedFile || undefined,
    });

    setSending(true);
    try {
      if (selectedFile) {
        await postSupportMessageMedia(cabinetUserId, fio, selectedFile, caption || undefined);
        toast.success('Вложение отправлено');
      } else {
        await postSupportMessage(cabinetUserId, fio, caption);
        toast.success('Сообщение отправлено');
      }

      setText('');
      setSelectedFile(null);
      await onSent?.();
    } catch (error) {
      toast.error(getSupportChatErrorMessage(error));
    } finally {
      removePendingMessage(pendingId);
      setSending(false);
    }
  };

  return {
    text,
    setText,
    selectedFile,
    sending,
    fileInputRef,
    canSend: !sending && (Boolean(selectedFile) || Boolean(text.trim())),
    openFilePicker,
    handleFilePick,
    removeSelectedFile,
    send,
  };
}
